import t from 'react-translate';
import { ClosingReason } from './stream-ai-response';

export { ClosingReason };

// Server's own deadline for receiving our config message after connecting — mirrored
// here so a silently-hung connection fails fast on the client instead of waiting forever.
const READY_TIMEOUT_MS = 10000;

// A silent connection drop may never fire a close/error event, so treat prolonged
// silence from the server as a dead connection.
const STALL_TIMEOUT_MS = 20000;

// WS close codes for unrecoverable session/auth/config errors. Retrying won't help,
// so the caller resets the whole role-play instead of offering resume-and-retry.
// 4011 (avatar session missing) is deliberately NOT here — it's recoverable by
// re-minting the avatar session and reconnecting; see onAvatarSessionMissing.
const FATAL_CLOSE_CODES = new Set([4001, 4003, 4010]);
export const isFatalCloseCode = (code?: number): boolean => code != null && FATAL_CLOSE_CODES.has(code);

// Avatar session missing/expired server-side — the caller re-mints via the
// avatar-session endpoint and reconnects the turn socket.
const AVATAR_SESSION_MISSING_CLOSE_CODE = 4011;

export type VoiceStreamChunk = {
  content: string;
  isLastChunk: boolean;
  // Set on `done` only when the whole role-play is over, not just this turn.
  closingReason?: ClosingReason;
};

export type VoiceStreamHandlers = {
  onChunk: (chunk: VoiceStreamChunk) => void;
  onAudioDelta: (data: { audio: string; sequence: number; sentenceId: number }) => void;
  onAudioDone: (data: { totalSequences: number }) => void;
  onAudioError: (data: { error: string; sequence: number; sentenceId: number }) => void;
  onError: (err: any) => void;
  // Separate from onError: an STT failure on the learner's just-sent audio, recovered by
  // closing this connection and opening a fresh one to retry the turn — not a generic error.
  onTranscriptError: (err: any) => void;
  // Only this turn's connection is closing — caller reconnects before the next turn.
  // In video mode, turnId correlates the just-finished turn's learner video upload.
  onTurnComplete: (turnId?: string) => void;
  onSpeechStarted?: () => void;
  onTranscriptFinal?: () => void;
  // Video mode only: the LiveAvatar began/finished speaking this turn's reply.
  onAvatarSpeechStarted?: () => void;
  onAvatarSpeechEnded?: () => void;
  // Video mode only, non-fatal: this turn degraded to text-only avatar-wise.
  onAvatarError?: (error: string) => void;
  // Close code 4011 — recoverable: re-mint the avatar session, then reconnect.
  onAvatarSessionMissing?: () => void;
};

export type VoiceStreamConnection = {
  sendAudioChunk: (bytes: ArrayBuffer) => void;
  sendAudioEnd: () => void;
  // Video mode, opening turn only: tells the server the avatar's <video>
  // element is mounted and the intro is open, releasing the held opening
  // utterance so it can't play into an unattached WebRTC sink.
  sendAvatarReady: () => void;
  close: () => void;
};

type ConnectVoiceStreamParams = {
  aiApiEndpoint: string;
  token: string;
  abortSignal: AbortSignal;
  // Mutable so the caller can swap in a fresh set of callbacks for each conversational
  // turn without tearing down and reopening the socket.
  handlersRef: { current: VoiceStreamHandlers };
};

const toWebSocketUrl = (aiApiEndpoint: string, token: string) => (
  `${aiApiEndpoint.replace(/^http/, 'ws')}/role-play/learner/voice-stream?token=${encodeURIComponent(token)}`
);

// Decode role_play_id from the token so it always matches what the server checks it against.
export const decodeRolePlayIdFromToken = (token: string): string | null => {
  try {
    const payloadSegment = token.split('.')[1];
    const base64 = payloadSegment.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    const claims = JSON.parse(atob(padded));
    return claims.role_play_id != null ? String(claims.role_play_id) : null;
  } catch {
    return null;
  }
};

const connectVoiceStream = ({
  aiApiEndpoint,
  token,
  abortSignal,
  handlersRef,
}: ConnectVoiceStreamParams): Promise<VoiceStreamConnection> => new Promise((resolve, reject) => {
  if (!aiApiEndpoint) {
    const error = new Error(t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.AI_HOST_URL_MISSING_ERROR());
    handlersRef.current.onError(error);
    reject(error);
    return;
  }

  let ws: WebSocket;
  try {
    ws = new WebSocket(toWebSocketUrl(aiApiEndpoint, token));
  } catch (err) {
    handlersRef.current.onError(err);
    reject(err);
    return;
  }

  let isReady = false;
  // Set once we know a close is expected (abort, turn_complete, or an explicit close()
  // call) so onclose can tell a normal shutdown apart from a dropped connection.
  let expectingClose = false;
  let readyTimer: ReturnType<typeof setTimeout> | null = null;
  let stallTimer: ReturnType<typeof setTimeout> | null = null;
  // Stall watchdog only runs while waiting on the AI's reply, not while the learner speaks.
  let isAwaitingAiResponse = false;

  const clearReadyTimer = () => {
    if (readyTimer !== null) { clearTimeout(readyTimer); readyTimer = null; }
  };

  const clearStallTimer = () => {
    if (stallTimer !== null) { clearTimeout(stallTimer); stallTimer = null; }
  };

  const resetStallTimer = () => {
    clearStallTimer();
    if (!isAwaitingAiResponse) return;
    stallTimer = setTimeout(() => {
      if (expectingClose) return;
      expectingClose = true;
      ws.close();
      handlersRef.current.onError(new Error('Voice stream stalled — no response from server'));
    }, STALL_TIMEOUT_MS);
  };

  const onAbort = () => {
    expectingClose = true;
    clearReadyTimer();
    ws.close();
    if (!isReady) reject(new DOMException('Voice stream aborted', 'AbortError'));
  };
  abortSignal.addEventListener('abort', onAbort, { once: true });

  const settleConnectFailure = (err: unknown) => {
    clearReadyTimer();
    abortSignal.removeEventListener('abort', onAbort);
    handlersRef.current.onError(err);
    reject(err);
  };

  ws.onopen = () => {
    ws.send(JSON.stringify({ role_play_id: decodeRolePlayIdFromToken(token) }));
    readyTimer = setTimeout(() => {
      expectingClose = true;
      ws.close();
      settleConnectFailure(new Error('Timed out waiting for voice stream to become ready'));
    }, READY_TIMEOUT_MS);
  };

  ws.onerror = () => {
    // Browsers always follow an error event with a close event — post-ready failures
    // are reported once, from onclose below, to avoid double-firing onError.
    if (!isReady) {
      settleConnectFailure(new Error('Voice stream connection error'));
    }
  };

  ws.onclose = (e) => {
    clearStallTimer();
    clearReadyTimer();
    abortSignal.removeEventListener('abort', onAbort);
    if (expectingClose) return;
    if (!isReady) {
      settleConnectFailure(new Error('Voice stream closed before becoming ready'));
      return;
    }
    if (e.code === AVATAR_SESSION_MISSING_CLOSE_CODE && handlersRef.current.onAvatarSessionMissing) {
      handlersRef.current.onAvatarSessionMissing();
      return;
    }
    handlersRef.current.onError(Object.assign(new Error('Voice stream closed unexpectedly'), { code: e.code }));
  };

  ws.onmessage = (event: MessageEvent) => {
    if (typeof event.data !== 'string') return;
    // No-op until sendAudioEnd() arms the watchdog.
    resetStallTimer();

    let message: any;
    try {
      message = JSON.parse(event.data);
    } catch (err) {
      handlersRef.current.onError(err);
      return;
    }

    switch (message.type) {
      case 'ready': {
        isReady = true;
        clearReadyTimer();
        // Deliberately not removing onAbort here — it stays registered for the rest of
        // the connection's life so a session-level abort still closes the socket.
        resolve({
          sendAudioChunk: (bytes: ArrayBuffer) => {
            if (ws.readyState === WebSocket.OPEN) ws.send(bytes);
          },
          sendAudioEnd: () => {
            if (ws.readyState === WebSocket.OPEN) {
              ws.send(JSON.stringify({ type: 'audio_end' }));
              // AI should respond now — arm the stall watchdog.
              isAwaitingAiResponse = true;
              resetStallTimer();
            }
          },
          sendAvatarReady: () => {
            if (ws.readyState === WebSocket.OPEN) {
              ws.send(JSON.stringify({ type: 'avatar_ready' }));
            }
          },
          close: () => {
            expectingClose = true;
            clearReadyTimer();
            clearStallTimer();
            abortSignal.removeEventListener('abort', onAbort);
            ws.close();
          },
        });
        break;
      }
      case 'delta':
        handlersRef.current.onChunk({ content: message.content, isLastChunk: false });
        break;
      case 'done':
        handlersRef.current.onChunk({ content: '', isLastChunk: true, closingReason: message.closing_reason });
        break;
      case 'audio_delta':
        handlersRef.current.onAudioDelta({
          audio: message.audio,
          sequence: message.sequence,
          sentenceId: message.sentence_id,
        });
        break;
      case 'audio_done':
        handlersRef.current.onAudioDone({ totalSequences: message.total_sequences });
        break;
      case 'audio_error':
        handlersRef.current.onAudioError({
          error: message.error,
          sequence: message.sequence,
          sentenceId: message.sentence_id,
        });
        break;
      case 'error':
        handlersRef.current.onError(new Error(`Error from AI stream: ${message.error}`));
        break;
      case 'transcript_error':
        handlersRef.current.onTranscriptError(new Error(`Transcript error from AI stream: ${message.error}`));
        break;
      case 'turn_complete':
        // Just this turn ending, not the role-play — caller reconnects for the next turn.
        expectingClose = true;
        handlersRef.current.onTurnComplete(message.turn_id);
        break;
      case 'speech_started':
        handlersRef.current.onSpeechStarted?.();
        break;
      case 'transcript_final':
        handlersRef.current.onTranscriptFinal?.();
        break;
      case 'avatar_speech_started':
        handlersRef.current.onAvatarSpeechStarted?.();
        break;
      case 'avatar_speech_ended':
        handlersRef.current.onAvatarSpeechEnded?.();
        break;
      case 'avatar_error':
        handlersRef.current.onAvatarError?.(message.error);
        break;
      default:
        break;
    }
  };
});

export default connectVoiceStream;
